import api from '@/api';
import {rankCustomerMatches} from '@/customer-matches';
import {findItem, findPages} from '@/nav-helpers';

/**
 * The "find a page" box, shared by the drawer and the columns. It matches pages by their words.
 * For a user whose menu has the Customers page, text with an @ in it is an email: Enter looks the
 * customer up and opens their page, or asks which account when several have the email (open
 * accounts first, then closed ones newest first). Nothing is looked up until Enter is pressed.
 */
export default {
  data() {
    return {
      search: '',
      isLookingUp: false,
      /** the accounts an email matched when there was more than one, for the user to pick from */
      customerChoices: [],
    };
  },
  computed: {
    matches() {
      return findPages(this.sections, this.search);
    },
    canFindCustomers() {
      return Boolean(findItem(this.sections, 'Customers'));
    },
    /** the hint says what the box takes; it reads the same for a user who can't open customers */
    placeholder() {
      return this.canFindCustomers ? 'find a page, or a customer by email' : 'find a page';
    },
    /** the text as an email to look up, or '' when it isn't one or the user can't open customers */
    emailSearch() {
      const text = (this.search || '').trim();
      return this.canFindCustomers && text.includes('@') ? text : '';
    },
  },
  methods: {
    openFirstMatch() {
      if (this.emailSearch) {
        this.openCustomer(this.emailSearch);
        return;
      }
      const [first] = this.matches;
      if (first) {
        this.$router.push(this.location(first.item)).catch(() => {});
      }
    },
    /** opens the customer the email matches, asks which when several do, or says there is none */
    async openCustomer(email) {
      if (this.isLookingUp) {
        return;
      }
      this.isLookingUp = true;
      try {
        const {data} = await api.get('v2/customers/search', {params: {query: email, includeDeleted: true}});
        const matches = rankCustomerMatches(data || [], email);
        if (matches.length === 1) {
          this.openCustomerPage(matches[0]);
        } else if (matches.length > 1) {
          this.customerChoices = matches;
        } else {
          this.$store.commit('showSnackbar', {message: `no customer matches "${email}"`, color: 'warning'});
        }
      } catch (error) {
        console.error('customer lookup failed', error);
        this.$store.commit('showSnackbar', {message: `customer lookup failed: ${error.message}`, color: 'error'});
      } finally {
        this.isLookingUp = false;
      }
    },
    openCustomerPage(customer) {
      this.customerChoices = [];
      this.$router.push({name: 'Customer', params: {id: customer.uid}}).catch(() => {});
    },
  },
};
